/**
 * Edge-Local Configuration Hook
 *
 * Fetches the local STT/TTS service configuration from the backend
 * when running in edge-local deployment mode. Returns null when not
 * in edge-local mode.
 *
 * Used by voice components to determine whether to use local speech
 * services instead of cloud APIs (Deepgram, ElevenLabs).
 */

import { useState, useEffect } from 'react'
import { useFeatureStore } from '@/stores/featureStore'
import { apiClient } from '@/services/apiClient'

export interface EdgeLocalSTTConfig {
  provider: string
  wsPath: string
  model: string
  language: string
}

export interface EdgeLocalTTSConfig {
  provider: string
  basePath: string
  model: string
  defaultVoice: string
  sampleRate: number
  voices?: string[]
}

export interface EdgeLocalConfig {
  stt?: EdgeLocalSTTConfig
  tts?: EdgeLocalTTSConfig
}

export function useEdgeLocalConfig(): {
  isEdgeLocal: boolean
  config: EdgeLocalConfig | null
  isLoading: boolean
  /**
   * Whether this deployment has local TTS (SA-1392): true when the
   * edge-local read answered with a `tts` block, false on any other profile
   * or when the read settled without one, null until both are known.
   */
  localTts: boolean | null
} {
  const profile = useFeatureStore((s) => s.profile)
  const isLoaded = useFeatureStore((s) => s.isLoaded)
  const isEdgeLocal = profile === 'edge-local'

  const [config, setConfig] = useState<EdgeLocalConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    if (!isLoaded || !isEdgeLocal) {
      setConfig(null)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setSettled(false)

    apiClient
      .fetch('/api/voice/edge-local-config')
      .then(async (res) => {
        if (cancelled) return
        if (res.ok) {
          const data = await res.json()
          setConfig(data)
        }
      })
      .catch(() => {
        // Edge-local config not available — that's fine
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
          setSettled(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isLoaded, isEdgeLocal])

  const localTts = !isLoaded ? null : !isEdgeLocal ? false : settled ? !!config?.tts : null

  return { isEdgeLocal, config, isLoading, localTts }
}
